import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import IntelexFrame from "../intelex/IntelexFrame";
import { confirmAction } from "../intelex/ConfirmationDialog";
import {
  airportCode,
  dataItem,
  displayDate,
  intelexDeleteImage,
  intelexRequest,
  intelexUploadImage,
  reportDetailPath,
  vamsysPirepReviewUrl,
} from "../intelex/api";

function parseValue(value, fallback) {
  if (Array.isArray(value) || (value && typeof value === "object"))
    return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return value;
    }
  }
  return value ?? fallback;
}

function VamsysPirepLink({ id, compact = false }) {
  const href = vamsysPirepReviewUrl(id);
  if (!href) return null;
  return (
    <a
      className={`intelex-vamsys-pirep-link${compact ? " compact" : ""}`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      Open PIREP in vAMSYS
    </a>
  );
}

function answerText(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (Array.isArray(value)) return value.join(", ") || "-";
  if (value && typeof value === "object") return JSON.stringify(value);
  return value === undefined || value === null || value === ""
    ? "-"
    : String(value);
}

const pirepStatusLabel = (value) =>
  ({
    pending: "No PIREP action",
    accepted: "PIREP accepted",
    rejected: "PIREP rejected",
    invalidated: "PIREP invalidated",
  })[value] || "No PIREP action";
const reportDecision = (value) =>
  ({
    accepted: {
      label: "Accepted",
      title: "Report accepted",
      description: "Flight Data Monitoring has completed the review and accepted this report.",
    },
    rejected: {
      label: "Rejected",
      title: "Report rejected",
      description: "Flight Data Monitoring has completed the review and rejected this report.",
    },
    invalidated: {
      label: "Invalidated",
      title: "Linked PIREP invalidated",
      description: "Flight Data Monitoring invalidated the linked PIREP.",
    },
    pending: {
      label: "Pending",
      title: "Review pending",
      description: "Flight Data Monitoring has not recorded a final decision yet.",
    },
  })[value] || {
    label: "Pending",
    title: "Review pending",
    description: "Flight Data Monitoring has not recorded a final decision yet.",
  };
const actionLabel = (value) =>
  ({
    report_closed: "Report closed",
    report_reopened: "Report reopened",
    pirep_accepted: "PIREP accepted in vAMSYS",
    pirep_rejected: "PIREP rejected in vAMSYS",
    pirep_invalidated: "PIREP invalidated in vAMSYS",
  })[value] || value;

function AttachmentGallery({ attachments, compact = false }) {
  if (!attachments?.length) return null;
  return (
    <div className={`intelex-attachment-gallery${compact ? " compact" : ""}`}>
      {attachments.map((attachment) => (
        <a
          href={attachment.url}
          target="_blank"
          rel="noreferrer"
          key={attachment.id}
        >
          <img
            src={attachment.url}
            alt={attachment.name || "Report attachment"}
          />
          <span>{attachment.name || "Open image"}</span>
        </a>
      ))}
    </div>
  );
}

function AnswerValue({ field, value, attachments }) {
  if (field.type !== "image") return answerText(value);
  const ids = new Set((Array.isArray(value) ? value : []).map(String));
  const matched = (attachments || []).filter((attachment) =>
    ids.has(String(attachment.id)),
  );
  return matched.length ? <AttachmentGallery attachments={matched} /> : "-";
}

function ConversationComposer({ report, isAdmin, onSent }) {
  const inputRef = useRef(null);
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [requiresResponse, setRequiresResponse] = useState(false);
  const [state, setState] = useState({
    uploading: false,
    sending: false,
    error: "",
  });
  const pilotLocked = report.conversationClosed && !isAdmin;

  const upload = async (event) => {
    const files = [...(event.target.files || [])].slice(
      0,
      5 - attachments.length,
    );
    event.target.value = "";
    if (!files.length) return;
    setState((current) => ({ ...current, uploading: true, error: "" }));
    const uploaded = [];
    try {
      for (const file of files)
        uploaded.push(
          await intelexUploadImage(file, {
            purpose: "message",
            reportId: report.id,
          }),
        );
      setAttachments((current) => [...current, ...uploaded]);
      setState((current) => ({ ...current, uploading: false }));
    } catch (error) {
      setAttachments((current) => [...current, ...uploaded]);
      setState((current) => ({
        ...current,
        uploading: false,
        error: error.message || "The image could not be uploaded.",
      }));
    }
  };
  const remove = async (attachment) => {
    try {
      await intelexDeleteImage(attachment.id);
      setAttachments((items) =>
        items.filter((item) => String(item.id) !== String(attachment.id)),
      );
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error.message || "The image could not be removed.",
      }));
    }
  };
  const send = async (event) => {
    event.preventDefault();
    if (!body.trim() && !attachments.length) return;
    setState((current) => ({ ...current, sending: true, error: "" }));
    try {
      await intelexRequest(`/api/portal/reports/${report.id}/messages`, {
        method: "POST",
        body: {
          body,
          attachmentIds: attachments.map((attachment) => attachment.id),
          requiresResponse: isAdmin && requiresResponse,
        },
      });
      setBody("");
      setAttachments([]);
      setRequiresResponse(false);
      setState({ uploading: false, sending: false, error: "" });
      await onSent();
    } catch (error) {
      setState((current) => ({
        ...current,
        sending: false,
        error: error.message || "The message could not be sent.",
      }));
    }
  };

  if (pilotLocked)
    return (
      <div className="intelex-conversation-closed">
        This report is closed. Pilot replies are no longer available.
      </div>
    );
  return (
    <form className="intelex-conversation-composer" onSubmit={send}>
      <label htmlFor="intelex-conversation-message">New message</label>
      <textarea
        id="intelex-conversation-message"
        rows="5"
        maxLength="12000"
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder="Write a response or ask a question"
      />
      {attachments.length > 0 && (
        <div className="intelex-pending-attachments">
          {attachments.map((attachment) => (
            <figure key={attachment.id}>
              <img src={attachment.url} alt={attachment.name} />
              <figcaption>
                <span>{attachment.name}</span>
                <button type="button" onClick={() => remove(attachment)}>
                  Remove
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {state.error && (
        <div className="intelex-alert" role="alert">
          {state.error}
        </div>
      )}
      <div className="intelex-composer-actions">
        <div>
          <button
            type="button"
            className="intelex-secondary-button"
            onClick={() => inputRef.current?.click()}
            disabled={state.uploading || attachments.length >= 5}
          >
            {state.uploading ? "Uploading..." : "Attach images"}
          </button>
          <input
            ref={inputRef}
            className="sr-only"
            type="file"
            accept="image/jpeg,image/png"
            multiple
            onChange={upload}
          />
          {isAdmin && (
            <label className="intelex-mandatory-toggle">
              <input
                type="checkbox"
                checked={requiresResponse}
                disabled={report.status === "closed"}
                onChange={(event) => setRequiresResponse(event.target.checked)}
              />
              <span>Require a pilot response to this message</span>
            </label>
          )}
        </div>
        <button
          className="intelex-primary-button"
          type="submit"
          disabled={
            state.sending ||
            state.uploading ||
            (!body.trim() && !attachments.length)
          }
        >
          {state.sending ? "Sending..." : "Send message"}
        </button>
      </div>
    </form>
  );
}

function MessageCard({ message, reportId, isAdmin, onDeleted }) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [state, setState] = useState({ deleting: false, error: "" });
  const remove = async (event) => {
    event.preventDefault();
    setState({ deleting: true, error: "" });
    try {
      await intelexRequest(
        `/api/portal/reports/${reportId}/messages/${message.id}`,
        { method: "DELETE", body: { reason } },
      );
      await onDeleted();
    } catch (error) {
      setState({
        deleting: false,
        error: error.message || "The message could not be deleted.",
      });
    }
  };
  if (message.deletedAt)
    return (
      <article className="intelex-message-deleted">
        <header>
          <strong>Message deleted</strong>
          <time>{displayDate(message.deletedAt)}</time>
        </header>
        <p>The message and its attachments were removed.</p>
        {isAdmin && message.deletionReason && (
          <small>Reason: {message.deletionReason}</small>
        )}
      </article>
    );
  return (
    <article>
      <header>
        <div>
          <strong>{message.author}</strong>
        </div>
        <div>
          <time>{displayDate(message.createdAt)}</time>
          {message.canDelete && (
            <button
              type="button"
              onClick={() => setConfirming((value) => !value)}
            >
              Delete
            </button>
          )}
        </div>
      </header>
      {message.body && <p>{message.body}</p>}
      {message.requiresResponse && <small>A pilot response is required.</small>}
      <AttachmentGallery attachments={message.attachments} compact />
      {confirming && (
        <form className="intelex-message-delete" onSubmit={remove}>
          <label>
            <span>{isAdmin ? "Deletion reason" : "Reason (optional)"}</span>
            <input
              required={isAdmin}
              maxLength="500"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Explain why this message should be removed"
            />
          </label>
          {state.error && (
            <div className="intelex-alert" role="alert">
              {state.error}
            </div>
          )}
          <div>
            <button type="button" onClick={() => setConfirming(false)}>
              Cancel
            </button>
            <button
              className="danger"
              type="submit"
              disabled={state.deleting || (isAdmin && !reason.trim())}
            >
              {state.deleting ? "Deleting..." : "Delete message"}
            </button>
          </div>
        </form>
      )}
    </article>
  );
}

function Conversation({ report, isAdmin, onUpdated }) {
  const count = (report.messages || []).filter(
    (message) => !message.deletedAt,
  ).length;
  return (
    <section className="intelex-conversation">
      <header>
        <div>
          <h2>Report messages</h2>
          <p>
            This discussion is separate from the FDM review and any vAMSYS
            action.
          </p>
        </div>
        <span>{count} messages</span>
      </header>
      {report.responseRequired && (
        <div className="intelex-response-required">
          A response from the pilot is required.
        </div>
      )}
      <div className="intelex-message-list">
        {report.messages?.length ? (
          report.messages.map((message) => (
            <MessageCard
              key={message.id}
              message={message}
              reportId={report.id}
              isAdmin={isAdmin}
              onDeleted={onUpdated}
            />
          ))
        ) : (
          <div className="intelex-empty compact">
            <p>No messages have been added.</p>
          </div>
        )}
      </div>
      <ConversationComposer
        report={report}
        isAdmin={isAdmin}
        onSent={onUpdated}
      />
    </section>
  );
}

function ActionHistory({ actions }) {
  if (!actions?.length) return null;
  return (
    <section className="intelex-action-history">
      <h3>Decision history</h3>
      <div>
        {actions.map((action) => (
          <article key={action.id}>
            <div>
              <strong>{actionLabel(action.type)}</strong>
              <span>
                {action.actor} -{" "}
                {displayDate(action.completedAt || action.createdAt)}
              </span>
            </div>
            <p>{action.reason}</p>
            {action.externalStatus === "failed" && (
              <small>
                vAMSYS action failed: {action.externalError || "Unknown error"}
              </small>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function ReportDecisionCard({ report }) {
  const decision = reportDecision(report.reviewStatus);
  const caseState =
    report.status === "closed"
      ? "Closed"
      : report.responseRequired
        ? "Awaiting pilot response"
        : "Open";
  return (
    <section
      className={`intelex-pilot-outcome intelex-decision-card intelex-pilot-outcome-${report.reviewStatus || "pending"}`}
      aria-label="Final decision"
    >
      <div className="intelex-decision-card-main">
        <header>
          <span>Final decision</span>
          <strong>{decision.label}</strong>
        </header>
        <h2>{decision.title}</h2>
        <div className="intelex-decision-reason">
          <span>Decision reason</span>
          <p>{report.reviewNote || decision.description}</p>
        </div>
        {report.isTestSubmission && (
          <small>Test report — this decision does not change any vAMSYS PIREP.</small>
        )}
      </div>
      <dl>
        <div>
          <dt>Case status</dt>
          <dd>{caseState}</dd>
        </div>
        <div>
          <dt>FDM reviewer</dt>
          <dd>{report.reviewedBy || "Not assigned"}</dd>
        </div>
        <div>
          <dt>Decision recorded</dt>
          <dd>{report.reviewedAt ? displayDate(report.reviewedAt) : "Not yet"}</dd>
        </div>
      </dl>
    </section>
  );
}

function AdminReview({ report, onUpdated }) {
  const [tab, setTab] = useState("report");
  const [decisionReason, setDecisionReason] = useState("");
  const [reportReason, setReportReason] = useState("");
  const [pirepReason, setPirepReason] = useState("");
  const [state, setState] = useState({ saving: "", error: "", success: "" });
  const reportClosed = report.status === "closed";
  const localDecisionAllowed = report.isTestSubmission || !report.pirepId;
  const recordDecision = async (decision) => {
    const label = decision === "pending" ? "return this report to pending review" : `${decision} this report`;
    if (
      !(await confirmAction({
        title: decision === "pending" ? "Return to pending review" : `${decision === "accepted" ? "Accept" : "Reject"} report`,
        message: `This will ${label}. The entered reason will be shown in the final decision card.`,
        confirmLabel:
          decision === "pending"
            ? "Mark as pending"
            : decision === "accepted"
              ? "Accept report"
              : "Reject report",
        tone: decision === "rejected" ? "danger" : "default",
      }))
    )
      return;
    setState({ saving: `decision-${decision}`, error: "", success: "" });
    try {
      await intelexRequest(`/api/portal/reports/${report.id}/review`, {
        method: "PUT",
        body: { decision, note: decisionReason },
      });
      setDecisionReason("");
      setState({
        saving: "",
        error: "",
        success:
          decision === "pending"
            ? "The report was returned to pending review."
            : `The report was ${decision}.`,
      });
      await onUpdated();
    } catch (error) {
      setState({
        saving: "",
        error: error.message || "The final decision could not be recorded.",
        success: "",
      });
    }
  };
  const changeReportState = async (event) => {
    event.preventDefault();
    const closed = !reportClosed;
    if (
      !(await confirmAction({
        title: `${closed ? "Close" : "Reopen"} report`,
        message: `${closed ? "Close" : "Reopen"} this report? The entered reason will be recorded in its history.`,
        confirmLabel: closed ? "Close report" : "Reopen report",
      }))
    )
      return;
    setState({ saving: "report", error: "", success: "" });
    try {
      await intelexRequest(`/api/portal/reports/${report.id}/state`, {
        method: "PUT",
        body: { closed, reason: reportReason },
      });
      setReportReason("");
      setState({
        saving: "",
        error: "",
        success: `The report was ${closed ? "closed" : "reopened"}.`,
      });
      await onUpdated();
    } catch (error) {
      setState({
        saving: "",
        error: error.message || "The report state could not be changed.",
        success: "",
      });
    }
  };
  const moderatePirep = async (action) => {
    const warning = {
      accept: "accept the linked PIREP",
      reject: "reject the linked PIREP",
      invalidate: "invalidate the linked PIREP and exclude it from statistics",
    }[action];
    if (
      !(await confirmAction({
        title: `${action[0].toUpperCase()}${action.slice(1)} linked PIREP`,
        message: `This will ${warning} in vAMSYS. This action affects the live PIREP record.`,
        confirmLabel: `${action[0].toUpperCase()}${action.slice(1)} PIREP`,
        tone: action === "accept" ? "default" : "danger",
      }))
    )
      return;
    setState({ saving: action, error: "", success: "" });
    try {
      const result = dataItem(
        await intelexRequest(`/api/portal/reports/${report.id}/pirep-action`, {
          method: "POST",
          body: { action, reason: pirepReason },
        }),
      );
      setPirepReason("");
      setState({
        saving: "",
        error: "",
        success: `PIREP #${result.pirepId} is now ${result.status} in vAMSYS.`,
      });
      await onUpdated();
    } catch (error) {
      setState({
        saving: "",
        error: error.message || "vAMSYS could not complete the PIREP action.",
        success: "",
      });
    }
  };
  return (
    <section className="intelex-review-panel intelex-admin-decision-panel">
      <header>
        <div>
          <h2>Flight Data Monitoring</h2>
          <p>
            The final decision and the open or closed case state are managed
            separately.
          </p>
        </div>
        <strong>{reportClosed ? "Report closed" : "Report open"}</strong>
      </header>
      <div
        className="intelex-review-tabs"
        role="tablist"
        aria-label="Flight Data Monitoring review sections"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === "report"}
          className={tab === "report" ? "active" : ""}
          onClick={() => setTab("report")}
        >
          Report decision
        </button>
        {report.pirepId && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === "pirep"}
            className={tab === "pirep" ? "active" : ""}
            onClick={() => setTab("pirep")}
          >
            Linked PIREP
          </button>
        )}
      </div>
      {state.error && (
        <div className="intelex-alert" role="alert">
          {state.error}
        </div>
      )}
      {state.success && (
        <div className="intelex-success" role="status">
          {state.success}
        </div>
      )}
      {tab === "report" ? (
        <div className="intelex-admin-review-content">
          {localDecisionAllowed ? (
            <div className="intelex-report-decision-form">
              <div className="intelex-decision-summary">
                <span>Final decision</span>
                <strong>{reportDecision(report.reviewStatus).label}</strong>
                <p>
                  {report.isTestSubmission
                    ? "This is a local test decision and will not change vAMSYS."
                    : "This report has no linked PIREP, so its outcome is recorded in Interep."}
                </p>
              </div>
              <label>
                <span>Decision reason</span>
                <textarea
                  required
                  maxLength="4000"
                  rows="5"
                  value={decisionReason}
                  onChange={(event) => setDecisionReason(event.target.value)}
                  placeholder="Explain the final decision"
                />
              </label>
              <div className="intelex-review-actions">
                <button
                  type="button"
                  className="intelex-primary-button"
                  disabled={state.saving || !decisionReason.trim()}
                  onClick={() => recordDecision("accepted")}
                >
                  Accept report
                </button>
                <button
                  type="button"
                  className="intelex-danger-button"
                  disabled={state.saving || !decisionReason.trim()}
                  onClick={() => recordDecision("rejected")}
                >
                  Reject report
                </button>
                {report.reviewStatus !== "pending" && (
                  <button
                    type="button"
                    className="intelex-secondary-button"
                    disabled={state.saving || !decisionReason.trim()}
                    onClick={() => recordDecision("pending")}
                  >
                    Return to pending
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="intelex-review-route-notice">
              <strong>Decision synchronized with vAMSYS</strong>
              <p>
                Use the Linked PIREP tab to accept, reject or invalidate the
                PIREP. The final decision card will update after vAMSYS confirms
                the action.
              </p>
              <button type="button" onClick={() => setTab("pirep")}>
                Open Linked PIREP review
              </button>
            </div>
          )}
          <form
            className="intelex-report-state-form"
            onSubmit={changeReportState}
          >
            <div className="intelex-decision-summary">
              <span>Case status</span>
              <strong>{reportClosed ? "Closed" : "Open"}</strong>
              <p>
                {reportClosed
                  ? "Pilot messages are locked until this case is reopened."
                  : "Closing locks pilot messaging and clears any outstanding response request."}
              </p>
            </div>
            <label>
              <span>
                {reportClosed ? "Reason for reopening" : "Reason for closing"}
              </span>
              <textarea
                required
                maxLength="4000"
                rows="5"
                value={reportReason}
                onChange={(event) => setReportReason(event.target.value)}
                placeholder="Record the reason for this status change"
              />
            </label>
            <div className="intelex-review-actions">
              <button
                className={
                  reportClosed
                    ? "intelex-primary-button"
                    : "intelex-danger-button"
                }
                type="submit"
                disabled={state.saving || !reportReason.trim()}
              >
                {state.saving === "report"
                  ? "Saving..."
                  : reportClosed
                    ? "Reopen report"
                    : "Close report"}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="intelex-pirep-moderation">
          {report.pirepId ? (
            <>
              <div className="intelex-decision-summary">
                <span>Linked PIREP</span>
                <strong>#{report.pirepId}</strong>
                <p>
                  Portal record: {pirepStatusLabel(report.reviewStatus)}. Each
                  action re-verifies the PIREP and pilot before changing vAMSYS.
                </p>
                <VamsysPirepLink id={report.pirepId} />
              </div>
              <label>
                <span>Decision reason</span>
                <textarea
                  required
                  maxLength="4000"
                  rows="5"
                  value={pirepReason}
                  onChange={(event) => setPirepReason(event.target.value)}
                  placeholder="Record why this PIREP is being moderated"
                />
              </label>
              <div className="intelex-pirep-actions">
                <button
                  type="button"
                  disabled={state.saving || !pirepReason.trim()}
                  onClick={() => moderatePirep("accept")}
                >
                  Accept in vAMSYS
                </button>
                <button
                  type="button"
                  className="danger"
                  disabled={state.saving || !pirepReason.trim()}
                  onClick={() => moderatePirep("reject")}
                >
                  Reject in vAMSYS
                </button>
                <button
                  type="button"
                  className="danger"
                  disabled={state.saving || !pirepReason.trim()}
                  onClick={() => moderatePirep("invalidate")}
                >
                  Invalidate in vAMSYS
                </button>
              </div>
            </>
          ) : (
            <div className="intelex-empty compact">
              <strong>No linked PIREP</strong>
              <p>This report cannot perform a vAMSYS moderation action.</p>
            </div>
          )}
        </div>
      )}
      <ActionHistory actions={report.actions} />
    </section>
  );
}

function ReportContent({ report, sections, answers, pirep }) {
  return (
    <>
      {pirep && (
        <section className="intelex-linked-flight-summary">
          <div>
            <span>Linked PIREP</span>
            <strong>#{pirep.id || report.pirepId}</strong>
          </div>
          <div>
            <span>Flight</span>
            <strong>{pirep.callsign || pirep.flightNumber || "-"}</strong>
          </div>
          <div>
            <span>Route</span>
            <strong>
              {airportCode(pirep.departureAirport || pirep.origin) || "-"} to{" "}
              {airportCode(pirep.arrivalAirport || pirep.destination) || "-"}
            </strong>
          </div>
          <div>
            <span>Date</span>
            <strong>
              {displayDate(pirep.departureTime || pirep.createdAt, false)}
            </strong>
          </div>
        </section>
      )}
      {!pirep && report.pirepId && (
        <section className="intelex-linked-flight-summary">
          <div>
            <span>Linked PIREP</span>
            <strong>#{report.pirepId}</strong>
          </div>
        </section>
      )}
      <article className="intelex-report-document">
        {Array.isArray(sections) && sections.length ? (
          sections.map((section, sectionIndex) => (
            <section key={section.id || sectionIndex}>
              <header>
                <span>{String(sectionIndex + 1).padStart(2, "0")}</span>
                <div>
                  <h2>{section.title || `Section ${sectionIndex + 1}`}</h2>
                  {section.description && <p>{section.description}</p>}
                </div>
              </header>
              <dl className="intelex-answer-grid">
                {(section.fields || []).map((field, fieldIndex) => {
                  const key = String(
                    field.id ||
                      field.key ||
                      `section_${sectionIndex + 1}_field_${fieldIndex + 1}`,
                  );
                  return (
                    <div
                      className={`intelex-answer intelex-width-${Number(field.width) || 12}`}
                      key={key}
                    >
                      <dt>{field.label || key}</dt>
                      <dd>
                        <AnswerValue
                          field={field}
                          value={answers[key]}
                          attachments={report.attachments}
                        />
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </section>
          ))
        ) : (
          <section>
            <header>
              <span>01</span>
              <div>
                <h2>Report information</h2>
              </div>
            </header>
            <dl className="intelex-answer-grid">
              {Object.entries(answers || {}).map(([key, value]) => (
                <div className="intelex-answer intelex-width-12" key={key}>
                  <dt>{key}</dt>
                  <dd>{answerText(value)}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}
      </article>
    </>
  );
}

function ReportDetail({ isAdmin }) {
  const { reportId, pirepId, eventNumber } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [view, setView] = useState("report");
  const [state, setState] = useState({ loading: true, error: "" });
  const [deletion, setDeletion] = useState({
    open: false,
    reason: "",
    working: false,
    error: "",
  });
  const loadReport = useCallback(async () => {
    try {
      const lookupPath = pirepId
        ? `/api/portal/reports/pirep/${encodeURIComponent(pirepId)}`
        : eventNumber
          ? `/api/portal/reports/reference/${encodeURIComponent(eventNumber)}`
          : `/api/portal/reports/${encodeURIComponent(reportId)}`;
      const loaded = dataItem(await intelexRequest(lookupPath));
      setReport(loaded);
      if (reportId) {
        navigate(reportDetailPath(loaded), {
          replace: true,
          state: window.history.state?.usr,
        });
      }
      setState({ loading: false, error: "" });
    } catch (error) {
      setState({
        loading: false,
        error: error.message || "This report could not be loaded.",
      });
    }
  }, [eventNumber, navigate, pirepId, reportId]);
  useEffect(() => {
    loadReport();
  }, [loadReport]);
  const deleteReport = async (event) => {
    event.preventDefault();
    if (
      !deletion.reason.trim() ||
      !(await confirmAction({
        title: "Delete report permanently",
        message:
          "Delete this report together with all messages and attachments? This cannot be undone.",
        confirmLabel: "Delete report",
        tone: "danger",
      }))
    )
      return;
    setDeletion((current) => ({ ...current, working: true, error: "" }));
    try {
      await intelexRequest(
        `/api/portal/reports/${encodeURIComponent(report.id)}`,
        { method: "DELETE", body: { reason: deletion.reason } },
      );
      navigate("/interep/admin/templates", {
        replace: true,
        state: { reportDeleted: true },
      });
    } catch (error) {
      setDeletion((current) => ({
        ...current,
        working: false,
        error: error.message || "The report could not be deleted.",
      }));
    }
  };
  const template = useMemo(
    () => parseValue(report?.template || report?.templateSnapshot, {}),
    [report],
  );
  const sections = useMemo(
    () => parseValue(template?.sections || report?.sections, []),
    [report, template],
  );
  const answers = useMemo(() => parseValue(report?.answers, {}), [report]);
  const pirep = useMemo(
    () => parseValue(report?.pirep || report?.pirepSnapshot, null),
    [report],
  );
  if (state.loading)
    return (
      <div className="intelex-main">
        <div className="intelex-loading" role="status">
          Loading report...
        </div>
      </div>
    );
  if (!report || state.error)
    return (
      <div className="intelex-main">
        <div className="intelex-alert" role="alert">
          {state.error || "This report is unavailable."}
        </div>
        <Link className="intelex-secondary-button" to="/interep/reports">
          Back to reports
        </Link>
      </div>
    );
  const messageCount = (report.messages || []).filter(
    (message) => !message.deletedAt,
  ).length;
  return (
    <div className="intelex-main intelex-report-detail-page">
      <div className="intelex-breadcrumbs">
        <Link to={isAdmin ? "/interep/admin/reviews" : "/interep/reports"}>
          {isAdmin ? "Review queue" : "Submitted reports"}
        </Link>
        <span aria-hidden="true">/</span>
        <span>{report.eventNumber || `Report #${report.id}`}</span>
      </div>
      <header className="intelex-report-document-head">
        <div>
          <h1>
            {template.title ||
              report.templateTitle ||
              report.title ||
              "Air Safety report"}
          </h1>
          <p>
            {report.eventNumber || report.reference || `Report #${report.id}`}
          </p>
          {report.isTestSubmission && (
            <span className="intelex-test-badge">Test submission</span>
          )}
        </div>
        <div>
          <strong className="intelex-review-text">
            {report.status === "closed" ? "Report closed" : "Report open"}
          </strong>
          <dl>
            <div>
              <dt>Submitted by</dt>
              <dd>{report.submittedBy || "-"}</dd>
            </div>
            <div>
              <dt>vAMSYS user</dt>
              <dd>
                {report.submittedByVamsysUserId
                  ? `#${report.submittedByVamsysUserId}`
                  : "Local account"}
              </dd>
            </div>
            <div>
              <dt>Submitted</dt>
              <dd>{displayDate(report.submittedAt || report.createdAt)}</dd>
            </div>
            <div>
              <dt>PIREP</dt>
              <dd>
                {report.pirepId ? `#${report.pirepId}` : "-"}
                {isAdmin && report.pirepId && (
                  <VamsysPirepLink id={report.pirepId} compact />
                )}
              </dd>
            </div>
          </dl>
        </div>
      </header>
      <ReportDecisionCard report={report} />
      <nav className="intelex-detail-tabs" aria-label="Report sections">
        <button
          type="button"
          className={view === "report" ? "active" : ""}
          onClick={() => setView("report")}
        >
          Report
        </button>
        <button
          type="button"
          className={view === "messages" ? "active" : ""}
          onClick={() => setView("messages")}
        >
          Messages <span>{messageCount}</span>
          {report.responseRequired && <i aria-label="Response required" />}
        </button>
        {isAdmin && (
          <button
            type="button"
            className={view === "review" ? "active" : ""}
            onClick={() => setView("review")}
          >
            FDM review
          </button>
        )}
      </nav>
      {view === "report" && (
        <>
          <ReportContent
            report={report}
            sections={sections}
            answers={answers}
            pirep={pirep}
          />
        </>
      )}
      {view === "messages" && (
        <Conversation
          report={report}
          isAdmin={isAdmin}
          onUpdated={loadReport}
        />
      )}
      {view === "review" && isAdmin && (
        <AdminReview report={report} onUpdated={loadReport} />
      )}
      {deletion.open && isAdmin && (
        <form className="intelex-delete-report-confirm" onSubmit={deleteReport}>
          <div>
            <h2>Delete report permanently</h2>
            <p>
              This removes the report, its conversation and private attachments.
              The administrator action remains in the audit log.
            </p>
          </div>
          <label>
            <span>Deletion reason</span>
            <textarea
              required
              rows="4"
              value={deletion.reason}
              onChange={(event) =>
                setDeletion((current) => ({
                  ...current,
                  reason: event.target.value,
                }))
              }
              placeholder="Record why this report is being deleted"
            />
          </label>
          {deletion.error && (
            <div className="intelex-alert" role="alert">
              {deletion.error}
            </div>
          )}
          <div>
            <button
              type="button"
              className="intelex-secondary-button"
              onClick={() =>
                setDeletion({
                  open: false,
                  reason: "",
                  working: false,
                  error: "",
                })
              }
            >
              Cancel
            </button>
            <button
              type="submit"
              className="intelex-danger-button"
              disabled={deletion.working || !deletion.reason.trim()}
            >
              {deletion.working ? "Deleting..." : "Delete report"}
            </button>
          </div>
        </form>
      )}
      <div className="intelex-document-actions">
        <Link
          className="intelex-secondary-button"
          to={isAdmin ? "/interep/admin/reviews" : "/interep/reports"}
        >
          Back
        </Link>
        {view === "report" && (
          <button
            type="button"
            className="intelex-text-button"
            onClick={() => window.print()}
          >
            Print report
          </button>
        )}
        {isAdmin && (
          <button
            type="button"
            className="intelex-danger-text-button"
            onClick={() =>
              setDeletion((current) => ({ ...current, open: true }))
            }
          >
            Delete report
          </button>
        )}
      </div>
    </div>
  );
}

export default function IntelexReportDetailPage() {
  return (
    <IntelexFrame active="reports" title="Report detail">
      {({ isAdmin }) => <ReportDetail isAdmin={isAdmin} />}
    </IntelexFrame>
  );
}
